/*
 * Copyright (C) 2026 - Potentially Ltd
 *
 * Please see distribution for license.
 */

import { Component, inject, signal } from '@angular/core';
import { FormControl, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatDialogRef } from '@angular/material/dialog';
import { EMBEDS_CONTENT_GENERATOR_SERVICE } from '@app/app/editor/services/embeds-content-generator.service';
import { LucideX } from '@lucide/angular';
import { TranslocoModule } from '@ngneat/transloco';
import { take } from 'rxjs';
import { DEFAULT_URL_VALIDATION_PATTERN } from '../../constants/constants';
import { SpecialEmbedsHelper } from '../../helpers/embeds/special-embeds.helper';
import { ExternalContentSummary } from '../../models';
import { StyleguideModule } from '../../styleguide/styleguide.module';

/**
 * "Add link" for V3 surfaces: paste a URL, and the dialog fetches its preview summary and closes with it.
 * Behaves like the V2 LinkDialogComponent and closes with the same `ExternalContentSummary`, so a call
 * site moves across by swapping the component and the dialog config.
 */
@Component({
  selector: 'ptl-v3-link-dialog',
  templateUrl: './v3-link-dialog.component.html',
  styleUrls: ['./v3-link-dialog.component.scss'],
  standalone: true,
  imports: [ReactiveFormsModule, TranslocoModule, StyleguideModule],
})
export class V3LinkDialogComponent {
  readonly LucideX = LucideX;

  readonly urlControl = new FormControl('', [Validators.required, Validators.pattern(DEFAULT_URL_VALIDATION_PATTERN)]);

  /** Errors wait for the first attempt, so an empty field doesn't open already marked as wrong. */
  readonly addAttempted = signal(false);
  readonly fetchingSummary = signal(false);
  readonly summaryUnavailable = signal(false);

  private readonly dialogRef = inject<MatDialogRef<V3LinkDialogComponent, ExternalContentSummary>>(MatDialogRef);
  private readonly embedsService = inject(EMBEDS_CONTENT_GENERATOR_SERVICE);

  showUrlError(): boolean {
    return this.addAttempted() && this.urlControl.invalid;
  }

  onUrlChanged(): void {
    this.summaryUnavailable.set(false);
  }

  onEnter(event: Event): void {
    event.preventDefault();
    this.onAdd();
  }

  onAdd(): void {
    this.addAttempted.set(true);
    if (this.urlControl.invalid || this.fetchingSummary()) {
      return;
    }

    const url = SpecialEmbedsHelper.correctUrl(this.urlControl.value ?? '');
    this.summaryUnavailable.set(false);
    this.fetchingSummary.set(true);

    const summary$ = this.embedsService.retrieveEmbeddableContentDetails(encodeURIComponent(url)).pipe(take(1));
    summary$.subscribe(({ value }) => {
      this.fetchingSummary.set(false);
      if (!value) {
        this.summaryUnavailable.set(true);
        return;
      }
      // Same cast as the V2 dialog: the API details are looser than ExternalContentSummary (nullable
      // fields, no position), and callers only read the shared fields. Aligning the models is its own change.
      const content = { ...value, type: 'EXTERNAL_CONTENT_SUMMARY' } as unknown as ExternalContentSummary;
      this.dialogRef.close(content);
    });
  }

  closeDialog(): void {
    this.dialogRef.close();
  }
}
